import axios from "axios";
import { ArrowLeft, Coins, Download, History, Megaphone, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Registry() {
  const [key, setKey] = useState(() => sessionStorage.getItem("dolo-registry-key") || "");
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [purgeArmed, setPurgeArmed] = useState(false);
  const purgeTimer = useRef(null);
  const [dropTitle, setDropTitle] = useState("");
  const [dropDetail, setDropDetail] = useState("");
  const [dropLink, setDropLink] = useState("");
  const [dropBusy, setDropBusy] = useState(false);
  const [dropStatus, setDropStatus] = useState("");
  const [ptsTag, setPtsTag] = useState("");
  const [ptsInfo, setPtsInfo] = useState(null);
  const [ptsValue, setPtsValue] = useState("");
  const [ptsStatus, setPtsStatus] = useState("");
  const [ptsBusy, setPtsBusy] = useState(false);
  const [ptsArmed, setPtsArmed] = useState(false);
  const ptsTimer = useRef(null);
  const [ptsBackupArmed, setPtsBackupArmed] = useState(false);
  const ptsBackupTimer = useRef(null);

  const sendDropAlert = async (event) => {
    event.preventDefault();
    setDropBusy(true);
    setDropStatus("");
    try {
      const { data } = await axios.post(
        `${API}/admin/drop-alert`,
        { title: dropTitle, detail: dropDetail || undefined, link: dropLink || undefined },
        { headers: { "X-Admin-Key": key } },
      );
      setDropStatus(
        data.sent > 0
          ? `DROP ALERT TRANSMITTED — ${data.sent} BADGE HOLDER${data.sent === 1 ? "" : "S"} REACHED.`
          : data.detail || "NO BADGE HOLDERS TO ALERT YET.",
      );
      setDropTitle("");
      setDropDetail("");
      setDropLink("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDropStatus(typeof detail === "string" ? detail : "TRANSMISSION FAILED — RETRY.");
    } finally {
      setDropBusy(false);
    }
  };

  const lookupPts = async () => {
    if (!ptsTag.trim()) return;
    setPtsBusy(true);
    setPtsStatus("");
    setPtsInfo(null);
    try {
      const { data } = await axios.get(`${API}/admin/points`, {
        params: { tag: ptsTag.trim() },
        headers: { "X-Admin-Key": key },
      });
      setPtsInfo(data);
      setPtsValue(String(data.points));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPtsStatus(typeof detail === "string" ? detail : "LOOKUP FAILED — RETRY.");
    } finally {
      setPtsBusy(false);
    }
  };

  const restorePts = async () => {
    if (!ptsInfo) return;
    if (!ptsArmed) {
      setPtsArmed(true);
      ptsTimer.current = window.setTimeout(() => setPtsArmed(false), 4000);
      return;
    }
    window.clearTimeout(ptsTimer.current);
    setPtsArmed(false);
    setPtsBusy(true);
    setPtsStatus("");
    try {
      const { data } = await axios.post(
        `${API}/admin/points`,
        { tag: ptsInfo.tag, points: Number(ptsValue) },
        { headers: { "X-Admin-Key": key } },
      );
      setPtsStatus(`VAULT RESTORED — ${data.tag} NOW HOLDS ${data.points} PTS (WAS ${data.before}).`);
      setPtsInfo((prev) => (prev ? { ...prev, points: data.points } : prev));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPtsStatus(typeof detail === "string" ? detail : "RESTORE FAILED — RETRY.");
    } finally {
      setPtsBusy(false);
    }
  };

  const restoreBackupPts = async () => {
    if (!ptsInfo || !ptsInfo.last_backup) return;
    if (!ptsBackupArmed) {
      setPtsBackupArmed(true);
      ptsBackupTimer.current = window.setTimeout(() => setPtsBackupArmed(false), 4000);
      return;
    }
    window.clearTimeout(ptsBackupTimer.current);
    setPtsBackupArmed(false);
    setPtsBusy(true);
    setPtsStatus("");
    try {
      const { data } = await axios.post(
        `${API}/admin/points/restore-backup`,
        { tag: ptsInfo.tag },
        { headers: { "X-Admin-Key": key } },
      );
      const msg = `VAULT ROLLED BACK — ${data.tag} RESTORED TO ${data.points} PTS FROM THE ${data.backup_date} SNAPSHOT (WAS ${data.before}). PTS, STREAK AND BADGES ALL RETURNED.`;
      await lookupPts();
      setPtsStatus(msg);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPtsStatus(typeof detail === "string" ? detail : "BACKUP RESTORE FAILED — RETRY.");
      setPtsBusy(false);
    }
  };

  const load = useCallback(async (adminKey) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/gate/registry`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setEntries(data.entries);
      sessionStorage.setItem("dolo-registry-key", adminKey);
    } catch (err) {
      setEntries(null);
      sessionStorage.removeItem("dolo-registry-key");
      setError(
        err.response?.status === 401
          ? "UNAUTHORIZED SIGNAL — REGISTRY SEALED."
          : "REGISTRY OFFLINE — RETRY TRANSMISSION.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (event) => {
    event.preventDefault();
    if (key.trim()) load(key.trim());
  };

  const exportCsv = () => {
    const rows = [["email", "entered_at"], ...entries.map((entry) => [entry.email, entry.created_at || ""])];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "dolo-runner-registry.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const purge = async () => {
    if (!purgeArmed) {
      setPurgeArmed(true);
      purgeTimer.current = window.setTimeout(() => setPurgeArmed(false), 4000);
      return;
    }
    window.clearTimeout(purgeTimer.current);
    setPurgeArmed(false);
    setLoading(true);
    try {
      await axios.delete(`${API}/gate/registry`, { headers: { "X-Admin-Key": key } });
      setEntries([]);
    } catch (err) {
      setError("PURGE FAILED — REGISTRY UNTOUCHED.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="registry-page" data-testid="registry-page">
      <section className="registry-section">
        <Link to="/" className="back-link" data-testid="registry-back-link">
          <ArrowLeft size={16} /> BACK TO DOLO'S WORLD
        </Link>
        <Reveal className="registry-panel">
          <div className="registry-head">
            <span>GATE CONTROL // SEALED RECORD</span>
            <span>RUNNER REGISTRY</span>
          </div>

          {!entries ? (
            <form className="registry-unlock" onSubmit={submit}>
              <ShieldCheck size={30} />
              <h1>Registry sealed.</h1>
              <p>Enter the gate control key to view every official email recorded by the entry gate.</p>
              <div className="registry-unlock-row">
                <input
                  type="password"
                  value={key}
                  onChange={(event) => setKey(event.target.value)}
                  placeholder="GATE CONTROL KEY"
                  className="gate-input"
                  data-testid="registry-key-input"
                  autoComplete="off"
                />
                <button type="submit" className="gate-button" disabled={loading} data-testid="registry-unlock-button">
                  {loading ? "CHECKING…" : "UNSEAL"}
                </button>
              </div>
              {error ? (
                <p className="gate-error" data-testid="registry-error">
                  {error}
                </p>
              ) : null}
            </form>
          ) : (
            <>
              <div className="registry-meta">
                <strong data-testid="registry-count">{entries.length} OFFICIAL SIGNAL{entries.length === 1 ? "" : "S"} RECORDED</strong>
                <div className="registry-actions">
                  <button
                    type="button"
                    className="registry-refresh"
                    onClick={exportCsv}
                    data-testid="registry-export-button"
                  >
                    <Download size={13} /> EXPORT CSV
                  </button>
                  <button
                    type="button"
                    className="registry-refresh"
                    onClick={() => load(key)}
                    disabled={loading}
                    data-testid="registry-refresh-button"
                  >
                    <RefreshCw size={13} /> REFRESH
                  </button>
                  <button
                    type="button"
                    className={`registry-refresh registry-purge ${purgeArmed ? "is-armed" : ""}`}
                    onClick={purge}
                    disabled={loading}
                    data-testid="registry-purge-button"
                  >
                    <Trash2 size={13} /> {purgeArmed ? "TAP AGAIN TO CONFIRM PURGE" : "PURGE REGISTRY"}
                  </button>
                </div>
              </div>
              <form className="drop-console" onSubmit={sendDropAlert} data-testid="drop-console">
                <strong className="drop-console-title">
                  <Megaphone size={14} /> DROP ALERT — EMAIL BADGE HOLDERS FIRST ACCESS
                </strong>
                <input
                  value={dropTitle}
                  onChange={(e) => setDropTitle(e.target.value)}
                  placeholder="DROP TITLE (E.G. TITAN TEE VOL. 2 IS LIVE)"
                  maxLength={80}
                  required
                  disabled={dropBusy}
                  data-testid="drop-title-input"
                />
                <input
                  value={dropDetail}
                  onChange={(e) => setDropDetail(e.target.value)}
                  placeholder="ONE-LINE DETAIL (OPTIONAL)"
                  maxLength={280}
                  disabled={dropBusy}
                  data-testid="drop-detail-input"
                />
                <input
                  value={dropLink}
                  onChange={(e) => setDropLink(e.target.value)}
                  placeholder="LINK (OPTIONAL — THIS SITE OR THE OFFICIAL STORE ONLY)"
                  disabled={dropBusy}
                  data-testid="drop-link-input"
                />
                <button type="submit" className="registry-refresh" disabled={dropBusy} data-testid="drop-send-button">
                  <Megaphone size={13} /> {dropBusy ? "TRANSMITTING…" : "TRANSMIT DROP ALERT"}
                </button>
                {dropStatus ? (
                  <p className="drop-console-status" data-testid="drop-status">
                    {dropStatus}
                  </p>
                ) : null}
              </form>
              <div className="drop-console pts-console" data-testid="pts-console">
                <strong className="drop-console-title">
                  <Coins size={14} /> PTS RESTORE — OWNER VAULT CONSOLE
                </strong>
                <div className="pts-console-row">
                  <input
                    value={ptsTag}
                    onChange={(e) => setPtsTag(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupPts(); } }}
                    placeholder="TITAN TAG (E.G. REBEL-MEGA-01)"
                    maxLength={40}
                    disabled={ptsBusy}
                    data-testid="pts-tag-input"
                    autoComplete="off"
                  />
                  <button type="button" className="registry-refresh" onClick={lookupPts} disabled={ptsBusy} data-testid="pts-lookup-button">
                    {ptsBusy && !ptsInfo ? "CHECKING…" : "LOOKUP"}
                  </button>
                </div>
                {ptsInfo ? (
                  <div className="pts-console-info" data-testid="pts-current">
                    <span>CURRENT BALANCE — <strong>{ptsInfo.points} PTS</strong></span>
                    <small>
                      STREAK {ptsInfo.streak || 0} · BADGES {(ptsInfo.badges || []).length}
                      {ptsInfo.last_backup ? ` · LAST BACKUP ${ptsInfo.last_backup.date}: ${ptsInfo.last_backup.points} PTS` : " · NO BACKUP SNAPSHOT YET"}
                    </small>
                    <div className="pts-console-row">
                      <input
                        type="number"
                        min="0"
                        max="1000000"
                        value={ptsValue}
                        onChange={(e) => setPtsValue(e.target.value)}
                        disabled={ptsBusy}
                        data-testid="pts-value-input"
                      />
                      <button
                        type="button"
                        className={`registry-refresh registry-purge ${ptsArmed ? "is-armed" : ""}`}
                        onClick={restorePts}
                        disabled={ptsBusy}
                        data-testid="pts-set-button"
                      >
                        {ptsArmed ? "TAP AGAIN TO CONFIRM RESTORE" : "SET EXACT PTS"}
                      </button>
                    </div>
                    {ptsInfo.last_backup ? (
                      <button
                        type="button"
                        className={`registry-refresh pts-backup-restore ${ptsBackupArmed ? "is-armed" : ""}`}
                        onClick={restoreBackupPts}
                        disabled={ptsBusy}
                        data-testid="pts-restore-backup-button"
                      >
                        <History size={13} />{" "}
                        {ptsBackupArmed
                          ? "TAP AGAIN TO CONFIRM ROLLBACK"
                          : `RESTORE FROM LAST BACKUP — ${ptsInfo.last_backup.date} (${ptsInfo.last_backup.points} PTS)`}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {ptsStatus ? (
                  <p className="drop-console-status" data-testid="pts-status">
                    {ptsStatus}
                  </p>
                ) : null}
              </div>
              <div className="registry-list" data-testid="registry-list">
                {entries.length === 0 ? (
                  <p className="registry-empty">No signals recorded yet. The gate is listening.</p>
                ) : (
                  entries.map((entry, index) => (
                    <div className="registry-row" data-testid={`registry-row-${index + 1}`} key={`${entry.email}-${entry.created_at}`}>
                      <span className="registry-index">{String(entries.length - index).padStart(3, "0")}</span>
                      <strong>{entry.email}</strong>
                      <small>{entry.created_at ? new Date(entry.created_at).toLocaleString() : "—"}</small>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </Reveal>
      </section>
      <Footer />
    </main>
  );
}
